import {
  Button,
  Card,
  Flex,
  Form,
  FormControl,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  FormErrorMessage,
  CurrencyInput,
  FormLabel,
  StackDivider,
  Stack,
  Link,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { format, isToday } from 'date-fns'
import { useState, useMemo, useEffect } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { formatCurrency } from '../../../helpers/formatters'
import { useDispatch, useSelector } from 'react-redux'
import { selectNewestPrequal } from '../../../store/selectors/prequal'
import { selectCustomerProfile } from '../../../store/selectors/customerProfile'
import { CustomerProfileData } from '../../../types/customerProfile'
import { selectBuyerId, selectBuyerInfo, selectBuyerInfoError } from '../../../store/selectors/buyerInfo'
import { BuyerInfo } from '../../../types/buyerInfo'
import { METHODS } from '../../../constants/affordability'
import { PrequalificationCard } from './PrequalificationCard'
import { selectClientIsBuyer } from '../../../store/selectors/client'
import { useHistory } from 'react-router'
import { useUpdateClient } from '../../../hooks/listings/useUpdateClient'
import { UPDATE_BUYER_INFO_SUCCESS } from '../../../actions/actionTypes'
import { hasCustomerToken } from '../../../auth'

const MSG = defineMessages({
  unsure: {
    id: 'for_you_content.budget_modal.unsure',
    defaultMessage: 'Not sure? Play with the'
  },
  affordabilityCalculator: {
    id: 'for_you_content.budget_modal.affordability_calculator',
    defaultMessage: 'Affordability Calculator'
  },
  title: {
    id: 'for_you_content.budget_modal.title',
    defaultMessage: 'Your Budget'
  },
  update: {
    id: 'for_you_content.budget_modal.update',
    defaultMessage: 'Update'
  },
  cancel: {
    id: 'for_you_content.budget_modal.cancel',
    defaultMessage: 'Cancel'
  },
  setBudget: {
    id: 'for_you_content.budget_modal.set_budget',
    defaultMessage: 'Set budget'
  },
  setBy: {
    id: 'for_you_content.budget_modal.set_by',
    defaultMessage: 'Set by {setBy} on {date}'
  },
  setByToday: {
    id: 'for_you_content.budget_modal.set_by_you_today',
    defaultMessage: 'Set by {setBy} today'
  },
  tuneYourBudget: {
    id: 'for_you_content.budget_modal.tune_your_budget',
    defaultMessage: 'Tune your budget'
  },
  yourBudget: {
    id: 'for_you_content.budget_modal.your_budget',
    defaultMessage: 'Your budget'
  },
  monthlyPayment: {
    id: 'for_you_content.budget_modal.monthly_payment',
    defaultMessage: '{amount}/mo (principal & interest)'
  },
  loanDetails: {
    id: 'for_you_content.budget_modal.loan_details',
    defaultMessage: '{loanType} • {loanTerm} yr fixed • {interestRate}%'
  },
  budgetRequired: {
    id: 'for_you_content.budget_modal.budget_required',
    defaultMessage: 'Budget is required'
  },
  enterValidNumber: {
    id: 'for_you_content.budget_modal.enter_valid_number',
    defaultMessage: 'Please enter a valid number'
  },
  none: {
    id: 'for_you_content.budget_modal.none',
    defaultMessage: 'None'
  },
  setYourBudget: {
    id: 'for_you_content.budget_modal.set_your_budget',
    defaultMessage: 'Set your budget'
  },
  yourLoanOfficer: {
    id: 'for_you_content.budget_modal.your_loan_officer',
    defaultMessage: 'Your loan officer'
  },
  you: {
    id: 'for_you_content.budget_modal.you',
    defaultMessage: 'you'
  },
  prequalAvailableByLine: {
    id: 'for_you_content.budget_modal.prequal_available_by_line',
    defaultMessage: 'Pre-qual is {prequalification}'
  },
  noPrequalByLine: {
    id: 'for_you_content.budget_modal.no_prequal_by_line',
    defaultMessage: 'Not set yet'
  },
  budgetUpdateSuccess: {
    id: 'for_you_content.budget_modal.update_success',
    defaultMessage: 'Budget updated successfully'
  },
  budgetUpdateError: {
    id: 'for_you_content.budget_modal.update_error',
    defaultMessage: 'There was an error updating your budget. Please try again.'
  },
  allset: {
    id: 'for_you_content.budget_modal.allset',
    defaultMessage: 'All set!'
  },
  uhoh: {
    id: 'for_you_content.budget_modal.uhoh',
    defaultMessage: 'Uh oh!'
  },
  prequalRequestSent: {
    id: 'for_you_content.budget_modal.prequal_request_sent',
    defaultMessage: 'Pre-qual request sent!'
  },
  willBeInTouchSoon: {
    id: 'for_you_content.budget_modal.will_be_in_touch_soon',
    defaultMessage: '{name} will be in touch soon'
  }
})

export const BudgetModal = () => {
  const intl = useIntl()
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false)
  const toast = useToast()
  const dispatch = useDispatch()
  const isImpersonating = hasCustomerToken()
  const history = useHistory()
  const { mutateAsync: updateClientInfo } = useUpdateClient()

  const prequalification = useSelector(selectNewestPrequal)
  const customerProfile: CustomerProfileData = useSelector(selectCustomerProfile)
  const buyerInfo: BuyerInfo = useSelector(selectBuyerInfo)
  const buyerInfoError = useSelector(selectBuyerInfoError)
  const isBuyer = useSelector(selectClientIsBuyer)
  const buyerId = useSelector(selectBuyerId)

  // Show error toast when there's an error
  useEffect(() => {
    if (buyerInfoError) {
      toast({
        title: intl.formatMessage(MSG.uhoh),
        description: intl.formatMessage(MSG.budgetUpdateError),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }, [buyerInfoError, intl, toast])

  const handleUpdateBudget = async (newBudget: number) => {
    const result = await updateClientInfo({
      input: {
        affordability: newBudget
      }
    })
    if (result.updateClient.updatedAt) {
      dispatch({
        type: UPDATE_BUYER_INFO_SUCCESS,
        data: {
          ...buyerInfo,
          affordability: newBudget
        }
      })
      toast({
        title: intl.formatMessage(MSG.allset),
        description: intl.formatMessage(MSG.budgetUpdateSuccess),
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      setIsBudgetModalOpen(false)
    }
  }

  const budgetByLine = useMemo(() => {
    if (buyerInfo?.affordability) {
      const isSetToday = isToday(new Date(buyerInfo?.updatedAt ?? new Date()))
      const setBy =
        buyerInfo?.affordabilityMethod === METHODS.PREQUAL
          ? customerProfile?.firstName ?? intl.formatMessage(MSG.yourLoanOfficer)
          : intl.formatMessage(MSG.you)

      return isSetToday
        ? intl.formatMessage(MSG.setByToday, { setBy })
        : intl.formatMessage(MSG.setBy, {
          setBy,
          date: format(
            new Date(buyerInfo?.updatedAt ?? new Date()),
            'MMMM d' +
            (new Date(buyerInfo?.updatedAt ?? new Date()).getFullYear() === new Date().getFullYear()
              ? ''
              : ', yyyy')
          )
        })
    } else if (Boolean(prequalification?.maxHomePrice)) {
      // This is for the case where the user has a prequalification but no budget
      // As of 4/24/2025, the budget is automatically set to the prequalification amount on a saved prequal
      return intl.formatMessage(MSG.prequalAvailableByLine, {
        prequalification: formatCurrency(prequalification.maxHomePrice)
      })
    } else {
      return intl.formatMessage(MSG.noPrequalByLine)
    }
  }, [buyerInfo, customerProfile, prequalification, intl])

  if (!isBuyer) {
    return null
  }

  return (
    <>
      <Card maxWidth={400} minWidth={['100%', '100%', 300]} px={4} py={1}>
        <Flex flexDirection='row' gap={3} justifyContent='space-between' alignItems='center'>
          <Flex flexDirection='column' gap={0}>
            <Heading variant='eyebrow' color={'brand'} textAlign={'left'} pb={0} mb={0} size={'sm'}>
              {intl.formatMessage(MSG.title)}
            </Heading>
            <Text color='neutral.900' fontWeight={'bold'} size={'lg'} pb={0} mb={0}>
              {buyerInfo?.affordability ? formatCurrency(buyerInfo.affordability) : intl.formatMessage(MSG.none)}
            </Text>
            <Text size='2xs' py={0} my={0}>
              {budgetByLine}
            </Text>
          </Flex>
          {!isImpersonating && (
            <Button
              onClick={() => {
                setIsBudgetModalOpen(true)
              }}
              tracking={{
                guid: '4hbYk7JnhHR8pG8z5MN15W',
                ui_context: 'For You Page',
                descriptor: 'Open Tune Budget Modal'
              }}
              size='sm'
            >
              {buyerInfo?.affordability ? intl.formatMessage(MSG.update) : intl.formatMessage(MSG.setYourBudget)}
            </Button>
          )}
        </Flex>
      </Card>

      <Modal size='lg' isOpen={isBudgetModalOpen} onClose={() => setIsBudgetModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>
            <Heading>{intl.formatMessage(MSG.tuneYourBudget)}</Heading>
          </ModalHeader>
          <ModalBody>
            <Stack divider={<StackDivider />}>
              <Form
                onSubmit={values => {
                  handleUpdateBudget(Number(values.budget))
                  setIsBudgetModalOpen(false)
                }}
                defaultValues={{
                  budget: buyerInfo?.affordability ? buyerInfo.affordability.toString() : ''
                }}
              >
                {({ formState: { errors } }) => (
                  <>
                    <FormControl isInvalid={!!errors.budget} mb={2}>
                      <FormLabel>{intl.formatMessage(MSG.yourBudget)}</FormLabel>
                      <CurrencyInput
                        placeholder={intl.formatMessage({
                          id: 'for_you_content.budget_modal.enter_budget_amount',
                          defaultMessage: 'Enter budget amount'
                        })}
                        name='budget'
                        rules={{
                          required: intl.formatMessage(MSG.budgetRequired),
                          pattern: {
                            value: /^\d+$/,
                            message: intl.formatMessage(MSG.enterValidNumber)
                          }
                        }}
                        tracking={{
                          guid: 'wXEFLAkshMuu9LWWpZycrZ',
                          ui_context: 'Budget Modal',
                          descriptor: 'Budget Input Changed'
                        }}
                      />
                      {errors.budget && <FormErrorMessage>{errors.budget.message}</FormErrorMessage>}
                    </FormControl>
                    {/* TODO: Add affordability calculator link when released, hidden for now*/}
                    {/* <Text size='sm'>
                      {intl.formatMessage({ id: 'for_you_content.budget_modal.not_sure', defaultMessage: 'Not sure?' })}{' '}
                      <Link
                        href='https://www.google.com'
                        fontWeight={'semibold'}
                        size='sm'
                        textDecor={'none'}
                        tracking={{
                          guid: '2LWG1J92yC4WReePgdhavs',
                          ui_context: 'Budget Modal',
                          descriptor: 'Affordability Calculator Link Clicked'
                        }}
                      >
                        {intl.formatMessage(MSG.affordabilityCalculator)}
                      </Link>
                    </Text> */}
                    <Text m={0} size='sm' textAlign='left'>
                      {intl.formatMessage(MSG.unsure)}{' '}
                      <Text
                        size='sm'
                        cursor='pointer'
                        onClick={() => {
                          history.push(`/calculators/${buyerId}/affordability-calculator`)
                        }}
                        as='a'
                        m={0}
                        fontWeight={'bold'}
                        color='primary.500'
                      >
                        {intl.formatMessage(MSG.affordabilityCalculator)}
                      </Text>
                    </Text>
                    <Flex mt={4} gap={3} justifyContent='flex-end'>
                      <Button
                        variant='ghost'
                        onClick={() => setIsBudgetModalOpen(false)}
                        tracking={{
                          guid: '9kHwAiFr4GH97RpstY9UwK',
                          ui_context: 'Budget Modal',
                          descriptor: 'Canceled budget update'
                        }}
                      >
                        {intl.formatMessage(MSG.cancel)}
                      </Button>
                      <Button
                        type='submit'
                        tracking={{
                          guid: '8kL9m2PqRs4tN7vX3wY6zA',
                          ui_context: 'Budget Modal',
                          descriptor: 'Submit Budget Update'
                        }}
                        colorScheme={'primary'}
                      >
                        {buyerInfo?.affordability ? intl.formatMessage(MSG.update) : intl.formatMessage(MSG.setBudget)}
                      </Button>
                    </Flex>
                  </>
                )}
              </Form>
              <PrequalificationCard />
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
